import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, TABLES, LEAD_STATUS, SCORE_THRESHOLDS } from '../config/supabase';
import { useAuth } from './AuthContext';
import GoogleCalendarService from '../services/googleCalendarService';
import emailAutomationService from '../services/emailAutomationService';
import advancedEmailAutomationService from '../services/advancedEmailAutomationService';
import logger from '../utils/logger';

const DataContext = createContext();

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}

export function DataProvider({ children }) {
  const { currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [leadEvents, setLeadEvents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leadEmailCounts, setLeadEmailCounts] = useState({});
  // CRITICAL FIX: CRM workspace operates exclusively in Helsinki timezone
  const [userTimezone, setUserTimezone] = useState(() => {
    const savedTimezone = localStorage.getItem('userTimezone');
    // Force Helsinki timezone for CRM workspace - no user timezone conversion
    return savedTimezone || 'Europe/Helsinki';
  });
  // Initialize readNotifications from localStorage
  const [readNotifications, setReadNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('readNotifications');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (error) {
      logger.error('Error loading read notifications:', error);
      return new Set();
    }
  });

  // Initialize readInboxMessages from localStorage (user-specific)
  // Now stores Map of conversationId -> readTimestamp for proper new reply detection
  const [readInboxMessages, setReadInboxMessages] = useState(new Map());

  // Load user-specific read inbox messages when currentUser changes
  useEffect(() => {
    if (currentUser?.email) {
      try {
        const storageKey = `readInboxMessages_${currentUser.email}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Handle both old format (array) and new format (object with timestamps)
          if (Array.isArray(parsed)) {
            // Migrate old format: convert array to Map with current timestamp
            const migratedMap = new Map();
            parsed.forEach(id => migratedMap.set(id, new Date().toISOString()));
            setReadInboxMessages(migratedMap);
          } else {
            // New format: object with timestamps
            setReadInboxMessages(new Map(Object.entries(parsed)));
          }
        } else {
          setReadInboxMessages(new Map());
        }
      } catch (error) {
        logger.error('Error loading read inbox messages:', error);
        setReadInboxMessages(new Map());
      }
    }
  }, [currentUser?.email]);

  // Fetch leads from Supabase with chunked loading to bypass 1000 row limit
  const fetchLeads = async () => {
    try {
      setLoading(true);
      
      // Fetch leads in chunks of 5000 to ensure all data is retrieved
      let allLeads = [];
      let hasMore = true;
      let offset = 0;
      const chunkSize = 10000;
      
      // Starting to fetch leads in chunks
      
      while (hasMore) {
        const { data: chunk, error: chunkError } = await supabase
          .from(TABLES.LEADS)
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + chunkSize - 1);
        
        if (chunkError) throw chunkError;
        
        if (chunk && chunk.length > 0) {
          allLeads = [...allLeads, ...chunk];
          offset += chunkSize;
          // Chunk fetched successfully
          
          // If we got less than chunkSize, we've reached the end
          if (chunk.length < chunkSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      // All leads fetched successfully

      // Fetch reply subjects from lead_events for all leads
      const { data: leadEventsData, error: eventsError } = await supabase
        .from(TABLES.LEAD_EVENTS)
        .select('lead_email, reply_subject')
        .not('reply_subject', 'is', null)
        .order('created_at', { ascending: true });
      
      if (eventsError) {
        // Error fetching lead events for reply subjects
      }

      // Process leads with reply subjects (all other fields already in leads table)
      const processedLeads = allLeads.map(lead => {
        const leadEvent = leadEventsData?.find(event => event.lead_email === lead.email);
        return {
          ...lead,
          // Ensure tags exist (should be in leads table now)
          tags: lead.tags || ['Lead', 'Prospect'],
          // Add reply_subject from lead_events
          reply_subject: leadEvent?.reply_subject || null
        };
      });
      
      setLeads(processedLeads);
      logger.log(`✅ Processed and set ${processedLeads.length} leads in state`);
      
      // Fetch individual lead email counts based on sequences
      try {
        const emailCounts = await advancedEmailAutomationService.getLeadEmailCounts();
        setLeadEmailCounts(emailCounts);
        logger.log('📊 Loaded lead email counts:', Object.keys(emailCounts).length, 'leads with sequences');
      } catch (countError) {
        logger.error('Error fetching lead email counts:', countError);
        setLeadEmailCounts({});
      }
      
      return processedLeads;
    } catch (error) {
      logger.error('Error fetching leads:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lead events
  // When leadEmail is provided, fetch ONLY for that lead WITHOUT updating global state
  // This prevents overwriting all events when viewing a single lead's details
  const fetchLeadEvents = async (leadEmail = null) => {
    try {
      // If fetching for specific lead, return data WITHOUT updating global state
      // This is critical - we don't want to overwrite all events with just one lead's events
      if (leadEmail) {
        let query = supabase
          .from(TABLES.LEAD_EVENTS)
          .select('*')
          .eq('lead_email', leadEmail)
          .order('created_at', { ascending: false });
        
        const { data, error } = await query;
        if (error) throw error;
        // DON'T call setLeadEvents here - just return the data for local use
        return data || [];
      }
      
      // For all events, fetch in chunks to bypass pagination limits
      let allEvents = [];
      let hasMore = true;
      let offset = 0;
      const chunkSize = 50000;
      
      logger.log('📥 Fetching lead events in chunks of', chunkSize);
      
      while (hasMore) {
        const { data: chunk, error: chunkError } = await supabase
          .from(TABLES.LEAD_EVENTS)
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + chunkSize - 1);
        
        if (chunkError) throw chunkError;
        
        if (chunk && chunk.length > 0) {
          allEvents = [...allEvents, ...chunk];
          offset += chunkSize;
          logger.log(`📊 Fetched ${chunk.length} events (Total: ${allEvents.length})`);
          
          if (chunk.length < chunkSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      logger.log(`✅ Finished fetching all ${allEvents.length} lead events`);
      setLeadEvents(allEvents);
      return allEvents;
    } catch (error) {
      logger.error('Error fetching lead events:', error);
      setError(error.message);
      return [];
    }
  };

  // Get dashboard stats with real-time database counting
  const getDashboardStats = () => {
    // Fallback to client-side calculation if database stats not available
    const totalLeads = leads.length;
    const callListLeads = leads.filter(lead => {
      if (lead.tags && lead.tags.includes('removed_from_call_list')) return false;
      if (lead.status === LEAD_STATUS.CALL_LIST) return true;
      if (lead.status === LEAD_STATUS.INTERESTED && lead.last_reply) return true;
      if ((lead.score || 0) >= SCORE_THRESHOLDS.CALL_LIST) return true;
      return false;
    }).length;
    const avgScore = leads.length > 0 
      ? Math.round(leads.reduce((sum, lead) => sum + (lead.score || 0), 0) / leads.length)
      : 0;
    
    // Count unique events per lead (one per lead, not total events)
    const uniqueOpens = new Set(leadEvents.filter(e => e.event_type === 'open').map(e => e.lead_email)).size;
    const uniqueClicks = new Set(leadEvents.filter(e => e.event_type === 'click').map(e => e.lead_email)).size;
    // Only count replies with actual reply message content
    const uniqueReplies = new Set(
      leadEvents
        .filter(e => e.event_type === 'reply' && e.reply_text && e.reply_text.trim().length > 0)
        .map(e => e.lead_email)
    ).size;
    
    // Count total sent emails (all events) - this will be supplemented by automation emails
    const totalSent = leadEvents.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent').length;

    const campaigns = [...new Set(leads.map(lead => lead.campaign_name).filter(Boolean))].length;

    return {
      totalLeads,
      callListLeads,
      avgScore,
      opens: uniqueOpens,
      clicks: uniqueClicks,
      replies: uniqueReplies,
      sent: totalSent,
      campaigns
    };
  };

  // Get real-time dashboard stats from database
  const getDashboardStatsRealtime = async () => {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_stats_realtime');
      
      if (error) {
        logger.error('Error fetching real-time dashboard stats:', error);
        return getDashboardStats(); // Fallback to client-side calculation
      }
      
      if (data && data.length > 0) {
        const stats = data[0];
        // Use client-side calculation for callListLeads and replies to ensure accurate filtering
        const clientSideStats = getDashboardStats();
        return {
          totalLeads: parseInt(stats.total_leads) || 0,
          callListLeads: clientSideStats.callListLeads, // Always use client-side calculation
          avgScore: parseFloat(stats.avg_score) || 0,
          opens: parseInt(stats.unique_opens) || 0,
          totalOpenEvents: parseInt(stats.total_open_events) || 0, // NEW: Total open events count
          clicks: parseInt(stats.unique_clicks) || 0,
          // IMPORTANT: Use client-side replies count to only include replies with actual message content
          replies: clientSideStats.replies,
          sent: parseInt(stats.total_sent) || 0,
          manualSent: parseInt(stats.total_manual_sent) || 0,
          automationSent: parseInt(stats.total_automation_sent) || 0,
          campaigns: parseInt(stats.total_campaigns) || 0
        };
      }
      
      return getDashboardStats(); // Fallback if no data
    } catch (error) {
      logger.error('Error in getDashboardStatsRealtime:', error);
      return getDashboardStats(); // Fallback to client-side calculation
    }
  };

  // Get real-time email counts from database
  const getRealtimeEmailCounts = async () => {
    try {
      const { data, error } = await supabase.rpc('get_realtime_email_counts');
      
      if (error) {
        logger.error('Error fetching real-time email counts:', error);
        return {
          totalManualSent: leadEvents.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent').length,
          totalAutomationSent: 0,
          totalSent: leadEvents.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent').length,
          uniqueManualLeads: new Set(leadEvents.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent').map(e => e.lead_email)).size,
          uniqueAutomationLeads: 0,
          uniqueTotalLeads: new Set(leadEvents.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent').map(e => e.lead_email)).size
        };
      }
      
      if (data && data.length > 0) {
        const counts = data[0];
        return {
          totalManualSent: parseInt(counts.total_manual_sent) || 0,
          totalAutomationSent: parseInt(counts.total_automation_sent) || 0,
          totalSent: parseInt(counts.total_sent) || 0,
          uniqueManualLeads: parseInt(counts.unique_manual_leads) || 0,
          uniqueAutomationLeads: parseInt(counts.unique_automation_leads) || 0,
          uniqueTotalLeads: parseInt(counts.unique_total_leads) || 0
        };
      }
      
      return {
        totalManualSent: 0,
        totalAutomationSent: 0,
        totalSent: 0,
        uniqueManualLeads: 0,
        uniqueAutomationLeads: 0,
        uniqueTotalLeads: 0
      };
    } catch (error) {
      logger.error('Error in getRealtimeEmailCounts:', error);
      return {
        totalManualSent: 0,
        totalAutomationSent: 0,
        totalSent: 0,
        uniqueManualLeads: 0,
        uniqueAutomationLeads: 0,
        uniqueTotalLeads: 0
      };
    }
  };

  // Get real-time campaign stats from database
  // IMPORTANT: Accept leadEventsData as parameter to ensure consistent data usage
  const getCampaignStatsRealtime = async (leadEventsData = null) => {
    try {
      const campaignEventStats = {};
      const eventsToProcess = leadEventsData || leadEvents || [];

      eventsToProcess.forEach((event) => {
        const campaignName = event.campaign_name || 'Unknown Campaign';
        const leadEmail = event.lead_email;
        if (!campaignEventStats[campaignName]) {
          campaignEventStats[campaignName] = {
            uniqueOpens: new Set(),
            uniqueClicks: new Set(),
            uniqueReplies: new Set(),
            uniqueSent: new Set(),
            totalSent: 0
          };
        }
        switch (event.event_type) {
          case 'open':
            if (leadEmail) campaignEventStats[campaignName].uniqueOpens.add(leadEmail);
            break;
          case 'click':
            if (leadEmail) campaignEventStats[campaignName].uniqueClicks.add(leadEmail);
            break;
          case 'reply':
            if (leadEmail && event.reply_text && event.reply_text.trim().length > 0) {
              campaignEventStats[campaignName].uniqueReplies.add(leadEmail);
            }
            break;
          case 'sent':
          case 'email_sent':
            if (leadEmail) campaignEventStats[campaignName].uniqueSent.add(leadEmail);
            campaignEventStats[campaignName].totalSent += 1;
            break;
          default:
            break;
        }
      });

      // Fetch base campaign data AND DB-validated reply counts in parallel
      const [baseResult, validRepliesResult] = await Promise.all([
        supabase.rpc('get_campaign_stats_realtime'),
        supabase.rpc('get_campaign_stats_with_valid_replies')
      ]);

      if (baseResult.error) {
        logger.error('Error fetching real-time campaign stats:', baseResult.error);
        return null;
      }

      // Build a map of DB-validated reply counts per campaign
      const dbValidReplies = {};
      if (!validRepliesResult.error && validRepliesResult.data && Array.isArray(validRepliesResult.data)) {
        validRepliesResult.data.forEach(row => {
          dbValidReplies[row.campaign_name] = parseInt(row.total_valid_replies) || 0;
        });
      }

      const data = baseResult.data;
      if (data && Array.isArray(data)) {
        return data.map((campaign) => {
          const name = campaign.campaign_name;
          const stats = campaignEventStats[name];
          const dbReplyCount = dbValidReplies[name];
          const clientReplyCount = stats ? stats.uniqueReplies.size : 0;
          const replies = dbReplyCount !== undefined ? dbReplyCount : clientReplyCount;

          return {
            name,
            leads: parseInt(campaign.total_leads) || 0,
            newLeads: parseInt(campaign.new_leads) || 0,
            interestedLeads: parseInt(campaign.interested_leads) || 0,
            callListLeads: parseInt(campaign.call_list_leads) || 0,
            archivedLeads: parseInt(campaign.archived_leads) || 0,
            avgScore: parseFloat(campaign.avg_score) || 0,
            totalScore: parseFloat(campaign.avg_score) * parseInt(campaign.total_leads) || 0,
            opens: stats ? stats.uniqueOpens.size : (parseInt(campaign.total_opens) || 0),
            clicks: stats ? stats.uniqueClicks.size : (parseInt(campaign.total_clicks) || 0),
            replies,
            sent: stats ? stats.totalSent : (parseInt(campaign.total_sent) || 0),
            sentLeads: stats ? stats.uniqueSent.size : 0,
            lastActivity: campaign.last_activity,
            createdAt: campaign.created_at
          };
        });
      }

      return [];
    } catch (error) {
      logger.error('Error in getCampaignStatsRealtime:', error);
      return null;
    }
  };

  // Get daily dashboard stats for today
  const getDailyDashboardStats = async (date = null) => {
    try {
      const { data, error } = await supabase.rpc('get_daily_dashboard_stats', {
        p_date: date || new Date().toISOString().split('T')[0]
      });

      if (error) {
        logger.error('Error fetching daily dashboard stats:', error);
        return null;
      }

      if (data && data.length > 0) {
        const stats = data[0];
        return {
          date: stats.stat_date,
          totalSent: parseInt(stats.total_sent) || 0,
          totalOpens: parseInt(stats.total_opens) || 0,
          totalReplies: parseInt(stats.total_replies) || 0,
          totalClicks: parseInt(stats.total_clicks) || 0,
          manualSent: parseInt(stats.manual_sent) || 0,
          automationSent: parseInt(stats.automation_sent) || 0,
          uniqueLeadsSent: parseInt(stats.unique_leads_sent) || 0,
          uniqueLeadsOpened: parseInt(stats.unique_leads_opened) || 0,
          uniqueLeadsReplied: parseInt(stats.unique_leads_replied) || 0,
          uniqueLeadsClicked: parseInt(stats.unique_leads_clicked) || 0
        };
      }

      return null;
    } catch (error) {
      logger.error('Error in getDailyDashboardStats:', error);
      return null;
    }
  };

  // Get dashboard stats for a date range
  const getDashboardStatsDateRange = async (startDate, endDate) => {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_stats_date_range', {
        p_start_date: startDate,
        p_end_date: endDate
      });
      
      if (error) {
        logger.error('Error fetching dashboard stats date range:', error);
        return [];
      }
      
      return data.map(stats => ({
        date: stats.stat_date,
        totalSent: parseInt(stats.total_sent) || 0,
        totalOpens: parseInt(stats.total_opens) || 0,
        totalReplies: parseInt(stats.total_replies) || 0,
        totalClicks: parseInt(stats.total_clicks) || 0,
        manualSent: parseInt(stats.manual_sent) || 0,
        automationSent: parseInt(stats.automation_sent) || 0,
        uniqueLeadsSent: parseInt(stats.unique_leads_sent) || 0,
        uniqueLeadsOpened: parseInt(stats.unique_leads_opened) || 0,
        uniqueLeadsReplied: parseInt(stats.unique_leads_replied) || 0,
        uniqueLeadsClicked: parseInt(stats.unique_leads_clicked) || 0
      }));
    } catch (error) {
      logger.error('Error in getDashboardStatsDateRange:', error);
      return [];
    }
  };

  // Archive daily stats to permanent storage
  const archiveDailyStatsToPermanent = async (date) => {
    try {
      const { data, error } = await supabase.rpc('archive_daily_stats_to_permanent', {
        p_date: date
      });
      
      if (error) {
        logger.error('Error archiving daily stats to permanent:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error('Error in archiveDailyStatsToPermanent:', error);
      return false;
    }
  };

  // Filter leads
  const filterLeads = (filters) => {
    return leads.filter(lead => {
      if (filters.status && lead.status !== filters.status) return false;
      if (filters.minScore && lead.score < filters.minScore) return false;
      if (filters.maxScore && lead.score > filters.maxScore) return false;
      if (filters.campaign && lead.campaign_name !== filters.campaign) return false;
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        return lead.email?.toLowerCase().includes(searchTerm) ||
               lead.first_name?.toLowerCase().includes(searchTerm) ||
               lead.company?.toLowerCase().includes(searchTerm);
      }
      return true;
    });
  };

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const { error } = await supabase
        .from(TABLES.LEADS)
        .update({ status: newStatus })
        .eq('id', leadId);
      
      if (error) throw error;
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        )
      );
    } catch (error) {
      logger.error('Error updating lead status:', error);
      setError(error.message);
    }
  };

  // Update lead contact information
  const updateLeadContact = async (leadId, contactData) => {
    try {
      // Update leads table with ALL fields (according to complete-crm-database-schema.sql)
      const leadsUpdateData = {
        email: contactData.email || null,
        first_name: contactData.firstName || contactData.first_name || null,
        last_name: contactData.lastName || contactData.last_name || null,
        company: contactData.company || null,
        job_title: contactData.jobTitle || contactData.job_title || null,
        phone: contactData.phone || null,
        linkedin: contactData.linkedin || null,
        website: contactData.website || null,
        location: contactData.location || null,
        industry: contactData.industry || null,
        company_size: contactData.companySize || contactData.company_size || null,
        annual_revenue: contactData.annualRevenue || contactData.annual_revenue || null,
        campaign_name: contactData.source || contactData.campaign_name || null,
        score: contactData.leadScore || contactData.score || 0,
        status: contactData.status || 'new',
        source: contactData.source || null,
        tags: Array.isArray(contactData.tags) ? contactData.tags : ['Lead', 'Prospect'],
        updated_at: new Date().toISOString()
      };

      // Remove undefined values and empty strings, but keep 0 for score and arrays for tags
      Object.keys(leadsUpdateData).forEach(key => {
        if (leadsUpdateData[key] === undefined || (leadsUpdateData[key] === '' && key !== 'score' && key !== 'tags')) {
          if (key === 'tags') {
            leadsUpdateData[key] = ['Lead', 'Prospect'];
          } else {
            leadsUpdateData[key] = null;
          }
        }
      });

      logger.log('Updating lead with data:', leadsUpdateData);

      const { error: leadsError } = await supabase
        .from(TABLES.LEADS)
        .update(leadsUpdateData)
        .eq('id', leadId);
      
      if (leadsError) {
        logger.error('Supabase leads error details:', leadsError);
        throw leadsError;
      }

      logger.log('✅ Lead updated successfully in leads table');
      
      // Update local state with the updated lead data
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId ? { ...lead, ...leadsUpdateData } : lead
        )
      );

      return { success: true, message: 'Lead contact information updated successfully' };
    } catch (error) {
      logger.error('❌ Error updating lead contact:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Bulk update lead status
  const bulkUpdateLeadStatus = async (leadIds, newStatus) => {
    try {
      const { error } = await supabase
        .from(TABLES.LEADS)
        .update({ status: newStatus })
        .in('id', leadIds);
      
      if (error) throw error;
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          leadIds.includes(lead.id) ? { ...lead, status: newStatus } : lead
        )
      );
      
      return { success: true, updatedCount: leadIds.length };
    } catch (error) {
      logger.error('Error bulk updating lead status:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Archive leads (bulk)
  const archiveLeads = async (leadIds) => {
    return await bulkUpdateLeadStatus(leadIds, LEAD_STATUS.ARCHIVED);
  };

  // Move leads to call list (bulk) - removes removal tags and sets status
  const moveToCallList = async (leadIds) => {
    try {
      // Process each lead to remove 'removed_from_call_list' tag
      const updatePromises = leadIds.map(async (leadId) => {
        const lead = leads.find(l => l.id === leadId);
        if (!lead) return { success: false, error: 'Lead not found' };

        // Remove 'removed_from_call_list' tag from existing tags
        const currentTags = lead.tags || [];
        const updatedTags = currentTags.filter(tag => tag !== 'removed_from_call_list');

        // Update lead with cleaned tags and call_list status
        const { error } = await supabase
          .from(TABLES.LEADS)
          .update({ 
            tags: updatedTags,
            status: LEAD_STATUS.CALL_LIST,
            updated_at: new Date().toISOString()
          })
          .eq('id', leadId);

        if (error) {
          logger.error('Error moving lead to call list:', error);
          return { success: false, error: error.message };
        }

        return { success: true };
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      if (failureCount > 0) {
        logger.error(`${failureCount} leads failed to move to call list`);
      }

      // Refresh leads data to reflect changes
      await fetchLeads();
      
      return { success: true, updatedCount: successCount };
    } catch (error) {
      logger.error('Error in moveToCallList:', error);
      return { success: false, error: error.message };
    }
  };

  // Fetch conversations for a specific lead
  const fetchConversations = async (leadEmail = null) => {
    try {
      let query = supabase
        .from(TABLES.CONVERSATIONS)
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (leadEmail) {
        query = query.eq('lead_email', leadEmail);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setConversations(data || []);
      return data || [];
    } catch (error) {
      logger.error('Error fetching conversations:', error);
      setError(error.message);
      return [];
    }
  };

  // Fetch messages for a specific conversation or lead
  const fetchMessages = async (conversationId = null, leadEmail = null) => {
    try {
      // If fetching for specific conversation or lead, use simple query
      if (conversationId || leadEmail) {
        let query = supabase
          .from(TABLES.MESSAGES)
          .select('*')
          .order('created_at', { ascending: true });
        
        if (conversationId) {
          query = query.eq('conversation_id', conversationId);
        } else if (leadEmail) {
          query = query.eq('lead_email', leadEmail);
        }
        
        const { data, error } = await query;
        if (error) throw error;
        setMessages(data || []);
        return data || [];
      }
      
      // For all messages, fetch in chunks to bypass pagination limits
      let allMessages = [];
      let hasMore = true;
      let offset = 0;
      const chunkSize = 10000;
      
      logger.log('📥 Fetching messages in chunks of', chunkSize);
      
      while (hasMore) {
        const { data: chunk, error: chunkError } = await supabase
          .from(TABLES.MESSAGES)
          .select('*')
          .order('created_at', { ascending: true })
          .range(offset, offset + chunkSize - 1);
        
        if (chunkError) throw chunkError;
        
        if (chunk && chunk.length > 0) {
          allMessages = [...allMessages, ...chunk];
          offset += chunkSize;
          logger.log(`📊 Fetched ${chunk.length} messages (Total: ${allMessages.length})`);
          
          if (chunk.length < chunkSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      logger.log(`✅ Finished fetching all ${allMessages.length} messages`);
      setMessages(allMessages);
      return allMessages;
    } catch (error) {
      logger.error('Error fetching messages:', error);
      setError(error.message);
      return [];
    }
  };

  // Send a new message
  const sendMessage = async (leadEmail, content, messageType = 'sent') => {
    // Prevent duplicate sends by checking if we're already sending
    const sendingKey = `${leadEmail}-${content.substring(0, 50)}-${messageType}`;
    
    try {
      if (window.sendingMessages && window.sendingMessages.has(sendingKey)) {
        logger.log('Duplicate message send prevented for:', leadEmail);
        return;
      }
      
      // Track sending state
      if (!window.sendingMessages) window.sendingMessages = new Set();
      window.sendingMessages.add(sendingKey);
      
      try {
        // Get or create conversation
        let conversation = conversations.find(c => c.lead_email === leadEmail);
        
        if (!conversation) {
          const lead = leads.find(l => l.email === leadEmail);
          const { data: newConversation, error: convError } = await supabase
            .from(TABLES.CONVERSATIONS)
            .insert({
              lead_id: lead?.id,
              lead_email: leadEmail,
              subject: 'Lead Conversation'
            })
            .select()
            .single();
          
          if (convError) throw convError;
          conversation = newConversation;
          setConversations(prev => [newConversation, ...prev]);
        }
        
        // Insert message
        const { data: message, error } = await supabase
          .from(TABLES.MESSAGES)
          .insert({
            conversation_id: conversation.id,
            lead_email: leadEmail,
            content: content,
            message_type: messageType,
            sender_type: messageType === 'sent' ? 'agent' : messageType,
            sender_email: 'sales@company.com',
            recipient_email: leadEmail
          })
          .select()
          .single();
        
        if (error) throw error;
        
        // Update local messages state
        setMessages(prev => [...prev, message]);
        
        // Update conversation timestamp
        await supabase
          .from(TABLES.CONVERSATIONS)
          .update({ updated_at: new Date().toISOString() })
          .eq('id', conversation.id);
        
        return message;
      } finally {
        // Clean up sending state after a delay
        setTimeout(() => {
          if (window.sendingMessages) {
            window.sendingMessages.delete(sendingKey);
          }
        }, 2000);
      }
    } catch (error) {
      logger.error('Error sending message:', error);
      setError(error.message);
      throw error;
    }
  };

  // Send email reply via n8n and Instantly
  const sendEmailReply = async (leadEmail, content, replyToMessageId = null) => {
    // Prevent duplicate sends by checking if we're already sending
    const sendingKey = `${leadEmail}-${content.substring(0, 50)}`;
    
    try {
      if (window.sendingEmails && window.sendingEmails.has(sendingKey)) {
        logger.log('Duplicate send prevented for:', leadEmail);
        return;
      }
      
      // Track sending state
      if (!window.sendingEmails) window.sendingEmails = new Set();
      window.sendingEmails.add(sendingKey);
      
      try {
        // Get lead data to determine the correct email account
        const lead = leads.find(l => l.email === leadEmail);
        if (!lead) {
          throw new Error('Lead not found');
        }

      // Get or create conversation
      let conversation = conversations.find(c => c.lead_email === leadEmail);
      
      if (!conversation) {
        const { data: newConversation, error: convError } = await supabase
          .from(TABLES.CONVERSATIONS)
          .insert({
            lead_id: lead.id,
            lead_email: leadEmail,
            subject: lead.reply_subject || `Re: ${lead.campaign_name}`,
            status: 'active'
          })
          .select()
          .single();
        
        if (convError) throw convError;
        conversation = newConversation;
        setConversations(prev => [newConversation, ...prev]);
      }

      // Find the original sender email from lead_events raw_data - ONLY use raw_data, no fallbacks
      let originalSenderEmail = null;
      let uniboxUrl = null;
      let originalSubject = null;
      let campaignId = null;
      let workspaceId = null;
      
      try {
        // Query lead_events to get the LATEST raw_data (most recent lead_event)
        const { data: leadEvent, error: eventError } = await supabase
          .from(TABLES.LEAD_EVENTS)
          .select('raw_data, reply_subject, campaign_id, workspace_id, created_at')
          .eq('lead_email', leadEmail)
          .not('raw_data', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (!eventError && leadEvent?.raw_data) {
          // Extract email_account from raw_data jsonb - primary source for sender email
          if (leadEvent.raw_data.email_account) {
            originalSenderEmail = leadEvent.raw_data.email_account;
          } else if (leadEvent.raw_data.reply_html) {
            // Fallback: extract sender email from reply_html if email_account is missing
            const emailMatch = leadEvent.raw_data.reply_html.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?=.*wrote:)/g);
            if (emailMatch && emailMatch.length > 0) {
              // Get the last email in the thread (original sender)
              originalSenderEmail = emailMatch[emailMatch.length - 1];
            } else {
              // No email_account or valid sender email found in raw_data for lead
              throw new Error(`No email_account found in lead_events raw_data for ${leadEmail}`);
            }
          } else {
            // No email_account or reply_html found in raw_data for lead
            throw new Error(`No email_account found in lead_events raw_data for ${leadEmail}`);
          }
          
          // Use the reply_subject from lead_events for dynamic subject
          if (leadEvent.reply_subject || leadEvent.raw_data.reply_subject) {
            originalSubject = leadEvent.reply_subject || leadEvent.raw_data.reply_subject;
          }
          
          // Extract unibox_url if available - important for thread continuity
          if (leadEvent.raw_data.unibox_url) {
            uniboxUrl = leadEvent.raw_data.unibox_url;
          }
          
          // Extract campaign and workspace info from raw_data
          if (leadEvent.raw_data.campaign_id || leadEvent.campaign_id) {
            campaignId = leadEvent.raw_data.campaign_id || leadEvent.campaign_id;
          }
          
          if (leadEvent.raw_data.workspace || leadEvent.workspace_id) {
            workspaceId = leadEvent.raw_data.workspace || leadEvent.workspace_id;
          }
          
          // Get thread info from raw_data - email_id is the message ID for replies
          if (leadEvent.raw_data.email_id && !replyToMessageId) {
            replyToMessageId = leadEvent.raw_data.email_id;
          }
        } else {
          // No raw_data found in lead_events for this lead
          if (eventError) {
            // Lead events query error
          }
          throw new Error(`No lead_events raw_data found for ${leadEmail}. Cannot send email without proper sender information.`);
        }
        
        // Validate that we have the essential data from raw_data
        if (!originalSenderEmail) {
          throw new Error(`Missing email_account in raw_data for lead: ${leadEmail}`);
        }
        
      } catch (error) {
        logger.error('Failed to extract data from lead_events raw_data:', error);
        setError(`Cannot send email reply: ${error.message}. Please ensure this lead has proper lead_events data with email_account information.`);
        setLoading(false);
        return;
      }

      // We now use ONLY the email account from raw_data, no database lookup needed
      
      // Get the most recent reply event raw_data for this lead
      let replyEventRawData = null;
      try {
        const { data: replyEvent, error: replyEventError } = await supabase
          .from(TABLES.LEAD_EVENTS)
          .select('raw_data, id, created_at')
          .eq('lead_email', leadEmail)
          .eq('event_type', 'reply')
          .not('raw_data', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (!replyEventError && replyEvent?.raw_data) {
          replyEventRawData = replyEvent.raw_data;
        }
      } catch (error) {
        logger.warn('Error fetching reply event raw_data:', error);
      }
      
      // Validate replyToMessageId - if invalid, don't include it to prevent 404 errors
      let validReplyToMessageId = null;
      if (replyToMessageId) {
        // Check if the message exists and has an instantly_message_id
        const { data: replyMessage } = await supabase
          .from(TABLES.MESSAGES)
          .select('instantly_message_id, id')
          .eq('id', replyToMessageId)
          .single();
        
        if (replyMessage && replyMessage.instantly_message_id) {
          validReplyToMessageId = replyMessage.instantly_message_id;
        } else {
          logger.warn('Invalid or missing reply message ID, sending as new thread');
        }
      }
      
      // If we have reply event raw_data, try to extract the email_id as reply_to_message_id
      if (replyEventRawData && !validReplyToMessageId) {
        // First priority: Extract email_id from the reply event raw_data
        const emailId = replyEventRawData.email_id;
        if (emailId) {
          validReplyToMessageId = emailId;
        } else {
          // Fallback: Extract other possible IDs from the reply event raw_data
          const replyToUuid = replyEventRawData.reply_to_uuid || replyEventRawData.d || replyEventRawData.message_id;
          if (replyToUuid) {
            validReplyToMessageId = replyToUuid;
          }
        }
      }
      
      // If still no valid reply ID, extract workspace ID from unibox_url as fallback
      if (!validReplyToMessageId && uniboxUrl) {
        try {
          const urlParams = new URLSearchParams(uniboxUrl.split('?')[1]);
          const workspaceId = urlParams.get('selected_wks');
          if (workspaceId) {
            validReplyToMessageId = workspaceId;
          }
        } catch (error) {
          // Error extracting workspace ID from unibox_url
        }
      }

      // Prepare the payload for n8n webhook - following the working pattern from aimanalishezanbusiness@gmail.com
      const webhookPayload = {
        action: 'send_email_reply',
        lead_data: {
          email: leadEmail,
          first_name: lead.first_name,
          campaign_name: lead.campaign_name || 'Unknown Campaign',
          campaign_id: campaignId || lead.campaign_id,
          workspace_id: workspaceId || lead.workspace_id,
          source: lead.source || 'Instantly'
        },
        email_data: {
          recipient_email: leadEmail,
          sender_email: originalSenderEmail,
          instantly_account_id: null,
          subject: originalSubject || conversation?.subject || `Re: ${lead.campaign_name}`,
          content: content,
          reply_to_message_id: validReplyToMessageId, // Only include if valid
          thread_id: lead.thread_id,
          unibox_url: uniboxUrl, // Include unibox URL from raw_data for thread continuity
          campaign_id: campaignId || lead.campaign_id,
          workspace_id: workspaceId || lead.workspace_id
        },
        reply_event_raw_data: replyEventRawData, // Include reply event raw_data for proper threading
        conversation_id: conversation.id,
        timestamp: new Date().toISOString()
      };

      // Send to n8n webhook
      const n8nWebhookUrl = process.env.REACT_APP_N8N_EMAIL_WEBHOOK_URL || process.env.REACT_APP_N8N_EMAIL_REPLY_WEBHOOK_URL || '';
      
      // Sending email reply webhook
      const response = await fetch(n8nWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      logger.log('n8n webhook response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Webhook failed:', response.status, errorText);
        throw new Error(`Email sending failed: ${response.status} - ${errorText}`);
      }
      
      // Check if response has content before parsing JSON
      let responseData = {};
      const responseText = await response.text();
      
      if (responseText && responseText.trim()) {
        try {
          responseData = JSON.parse(responseText);
        } catch (jsonError) {
          logger.warn('Response is not valid JSON, treating as success:', responseText);
          responseData = { success: true, message: responseText };
        }
      } else {
        logger.log('Empty response from webhook, treating as success');
        responseData = { success: true };
      }
      
      logger.log('✓ Email sent successfully via webhook using raw_data sender:', originalSenderEmail, responseData);

      // Insert the message into Supabase with 'sent' status after successful n8n response
      const { data: message, error } = await supabase
        .from(TABLES.MESSAGES)
        .insert({
          conversation_id: conversation.id,
          lead_email: leadEmail,
          sender_type: 'agent',
          content: content,
          message_type: 'email',
          sender_email: originalSenderEmail,
          recipient_email: leadEmail,
          email_status: 'sent',
          instantly_message_id: responseData.message_id || null,
          thread_id: lead.thread_id
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Update local messages state
      setMessages(prev => [...prev, message]);
      
      // Update conversation timestamp
      await supabase
        .from(TABLES.CONVERSATIONS)
        .update({ 
          last_message_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', conversation.id);
      
      return message;
      } finally {
        // Clean up sending state
        if (window.sendingEmails) {
          window.sendingEmails.delete(sendingKey);
        }
      }
    } catch (error) {
      logger.error('Error sending email reply (raw_data method):', error);
      setError(`Failed to send email reply using raw_data: ${error.message}`);
      // Clean up sending state on error
      if (window.sendingEmails) {
        window.sendingEmails.delete(sendingKey);
      }
      throw error;
    }
  };

  // Fetch meetings with booking status for a specific lead
  const fetchMeetings = async (leadEmail = null) => {
    try {
      let query = supabase
        .from(TABLES.BOOKING_MANAGEMENT)
        .select('*')
        .order('meeting_date', { ascending: true })
        .order('meeting_time', { ascending: true });
      
      if (leadEmail) {
        query = query.eq('lead_email', leadEmail);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setMeetings(data || []);
      return data || [];
    } catch (error) {
      logger.error('Error fetching meetings:', error);
      setError(error.message);
      return [];
    }
  };

  // Schedule a new meeting
  const scheduleMeeting = async (leadEmail, meetingData) => {
    try {
      logger.log('=== SCHEDULE MEETING DEBUG ===');
      logger.log('Received meetingData:', meetingData);
      logger.log('Lead email:', leadEmail);
      logger.log('Raw date from form:', meetingData.date);
      logger.log('Raw time from form:', meetingData.time);
      logger.log('Current browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
      logger.log('Current date/time:', new Date().toISOString());
      
      const lead = leads.find(l => l.email === leadEmail);
      logger.log('Found lead:', lead);
      
      const meetingToInsert = {
        lead_id: lead?.id,
        lead_email: leadEmail,
        title: meetingData.title,
        description: meetingData.description,
        meeting_date: meetingData.date,
        meeting_time: meetingData.time,
        duration: meetingData.duration || 30,
        meeting_type: meetingData.type || 'call',
        calendar_sync_status: meetingData.syncToGoogle ? 'not_synced' : 'not_synced'
      };
      
      logger.log('Data to insert into database:', meetingToInsert);
      logger.log('Meeting date being stored:', meetingToInsert.meeting_date);
      logger.log('Meeting time being stored:', meetingToInsert.meeting_time);
      
      const { data: meeting, error } = await supabase
        .from(TABLES.MEETINGS)
        .insert(meetingToInsert)
        .select()
        .single();
        
      logger.log('Inserted meeting result:', meeting);
      logger.log('Insert error (if any):', error);
      
      if (error) throw error;
      
      // Sync to Google Calendar if requested
      if (meetingData.syncToGoogle && GoogleCalendarService.isEnabled()) {
        try {
          await syncMeetingToGoogle(meeting);
        } catch (syncError) {
          logger.warn('Google Calendar sync failed:', syncError);
          // Update meeting status to indicate sync failure
          await supabase
            .from(TABLES.MEETINGS)
            .update({ calendar_sync_status: 'sync_failed' })
            .eq('id', meeting.id);
        }
      }
      
      // Update local meetings state
      setMeetings(prev => [...prev, meeting]);
      
      return meeting;
    } catch (error) {
      logger.error('Error scheduling meeting:', error);
      setError(error.message);
      throw error;
    }
  };

  // Sync meeting to Google Calendar
  const syncMeetingToGoogle = async (meeting) => {
    try {
      if (!GoogleCalendarService.isEnabled()) {
        throw new Error('Google Calendar integration is not enabled');
      }

      logger.log('Syncing meeting to Google Calendar:', meeting);
      
      // Update status to 'syncing' first
      await supabase
        .from(TABLES.MEETINGS)
        .update({ calendar_sync_status: 'syncing' })
        .eq('id', meeting.id);

      // Enhance meeting data with lead information for proper Google Calendar formatting
      const lead = leads.find(l => l.email === meeting.lead_email);
      const enhancedMeetingData = {
        ...meeting,
        contact_name: lead?.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : lead?.email || 'Contact',
        contact_email: lead?.email || meeting.lead_email
      };
      
      logger.log('Enhanced meeting data for Google Calendar:', enhancedMeetingData);

      const result = await GoogleCalendarService.createEvent(enhancedMeetingData, userTimezone);
      
      if (result && result.eventId) {
        // Update meeting with Google Calendar details
        const { error: updateError } = await supabase
          .from(TABLES.MEETINGS)
          .update({
            google_calendar_event_id: result.eventId,
            google_calendar_link: result.htmlLink,
            video_link: result.hangoutLink,
            calendar_sync_status: 'synced',
            last_synced_at: new Date().toISOString()
          })
          .eq('id', meeting.id);
        
        if (updateError) throw updateError;
        
        // Update local state
        setMeetings(prev => prev.map(m => 
          m.id === meeting.id 
            ? {
                ...m,
                google_calendar_event_id: result.eventId,
                google_calendar_link: result.htmlLink,
                video_link: result.hangoutLink,
                calendar_sync_status: 'synced',
                last_synced_at: new Date().toISOString()
              }
            : m
        ));
        
        return result;
      } else {
        throw new Error(result.error || 'Failed to create Google Calendar event');
      }
    } catch (error) {
      logger.error('Error syncing meeting to Google Calendar:', error);
      throw error;
    }
  };

  // Update existing meeting and sync to Google Calendar
  const updateMeetingWithSync = async (meetingId, updateData) => {
    try {
      const { data: meeting, error } = await supabase
        .from(TABLES.MEETINGS)
        .update(updateData)
        .eq('id', meetingId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Sync to Google Calendar if it was previously synced or sync is requested
      if ((meeting.google_calendar_event_id || updateData.syncToGoogle) && GoogleCalendarService.isEnabled()) {
        try {
          if (meeting.google_calendar_event_id) {
            // Update existing Google Calendar event
            await GoogleCalendarService.updateEvent(meeting, meeting.google_calendar_event_id);
          } else {
            // Create new Google Calendar event
            await syncMeetingToGoogle(meeting);
          }
        } catch (syncError) {
          logger.warn('Google Calendar sync failed:', syncError);
        }
      }
      
      // Update local meetings state
      setMeetings(prev => prev.map(m => m.id === meetingId ? meeting : m));
      
      return meeting;
    } catch (error) {
      logger.error('Error updating meeting:', error);
      setError(error.message);
      throw error;
    }
  };

  // Delete meeting and remove from Google Calendar
  const deleteMeetingWithSync = async (meetingId) => {
    try {
      const meeting = meetings.find(m => m.id === meetingId);
      
      // Remove from Google Calendar if synced
      if (meeting?.google_calendar_event_id && GoogleCalendarService.isEnabled()) {
        try {
          await GoogleCalendarService.deleteEvent(meeting.google_calendar_event_id);
        } catch (syncError) {
          logger.warn('Failed to remove from Google Calendar:', syncError);
        }
      }
      
      // Delete from database
      const { error } = await supabase
        .from(TABLES.MEETINGS)
        .delete()
        .eq('id', meetingId);
      
      if (error) throw error;
      
      // Update local meetings state
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
      
      return true;
    } catch (error) {
      logger.error('Error deleting meeting:', error);
      setError(error.message);
      throw error;
    }
  };

  // Fetch notes for a specific lead
  const fetchNotes = async (leadEmail = null) => {
    try {
      let query = supabase
        .from(TABLES.NOTES)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (leadEmail) {
        query = query.eq('lead_email', leadEmail);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setNotes(data || []);
      return data || [];
    } catch (error) {
      logger.error('Error fetching notes:', error);
      setError(error.message);
      return [];
    }
  };

  // Add a new note
  const addNote = async (leadEmail, content) => {
    try {
      const lead = leads.find(l => l.email === leadEmail);
      
      const { data: note, error } = await supabase
        .from(TABLES.NOTES)
        .insert({
          lead_id: lead?.id,
          lead_email: leadEmail,
          content: content,
          created_by: 'current_user' // Replace with actual user info
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Update local notes state
      setNotes(prev => [note, ...prev]);
      
      return note;
    } catch (error) {
      logger.error('Error adding note:', error);
      setError(error.message);
      throw error;
    }
  };

  // Fetch tasks for a specific lead
  const fetchTasks = async (leadEmail = null) => {
    try {
      let query = supabase
        .from(TABLES.TASKS)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (leadEmail) {
        query = query.eq('lead_email', leadEmail);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setTasks(data || []);
      return data || [];
    } catch (error) {
      logger.error('Error fetching tasks:', error);
      setError(error.message);
      return [];
    }
  };

  // Add a new task
  const addTask = async (leadEmail, taskData) => {
    try {
      const lead = leads.find(l => l.email === leadEmail);
      
      const { data: task, error } = await supabase
        .from(TABLES.TASKS)
        .insert({
          lead_id: lead?.id,
          lead_email: leadEmail,
          title: taskData.title,
          description: taskData.description,
          due_date: taskData.dueDate,
          priority: taskData.priority || 'medium',
          created_by: 'current_user' // Replace with actual user info
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Update local tasks state
      setTasks(prev => [task, ...prev]);
      
      return task;
    } catch (error) {
      logger.error('Error adding task:', error);
      setError(error.message);
      throw error;
    }
  };

  // Toggle task completion
  const toggleTask = async (taskId, completed) => {
    try {
      const { error } = await supabase
        .from(TABLES.TASKS)
        .update({ completed: completed })
        .eq('id', taskId);
      
      if (error) throw error;
      
      // Update local tasks state
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId ? { ...task, completed: completed } : task
        )
      );
    } catch (error) {
      logger.error('Error toggling task:', error);
      setError(error.message);
      throw error;
    }
  };

  // Restore leads from archive (bulk)
  const restoreLeads = async (leadIds) => {
    return await bulkUpdateLeadStatus(leadIds, LEAD_STATUS.NEW);
  };

  // Permanently delete leads (bulk)
  const deleteLeads = async (leadIds) => {
    try {
      // First, get the email addresses of leads to be deleted
      const { data: leadsToDelete, error: fetchError } = await supabase
        .from(TABLES.LEADS)
        .select('id, email')
        .in('id', leadIds);
      
      if (fetchError) throw fetchError;
      if (!leadsToDelete || leadsToDelete.length === 0) {
        return { success: true, deletedCount: 0 };
      }
      
      const leadEmails = leadsToDelete.map(lead => lead.email);
      logger.log(`🗑️ Deleting ${leadEmails.length} leads and their related records...`);
      
      // Delete related records in correct order (child tables first)
      // Order matters to avoid foreign key violations
      
      // 1. Delete follow-up calls
      const { error: callsError } = await supabase
        .from('email_automation_followup_calls')
        .delete()
        .in('lead_email', leadEmails);
      if (callsError) logger.warn('Error deleting follow-up calls:', callsError);
      
      // 2. Delete email tracking events
      const { error: trackingError } = await supabase
        .from('email_tracking_events')
        .delete()
        .in('lead_email', leadEmails);
      if (trackingError) logger.warn('Error deleting tracking events:', trackingError);
      
      // 3. Delete recent opens
      const { error: opensError } = await supabase
        .from('email_tracking_recent_opens')
        .delete()
        .in('lead_email', leadEmails);
      if (opensError) logger.warn('Error deleting recent opens:', opensError);
      
      // 4. Delete automation executions
      const { error: executionsError } = await supabase
        .from('email_automation_executions')
        .delete()
        .in('lead_email', leadEmails);
      if (executionsError) logger.warn('Error deleting automation executions:', executionsError);
      
      // 5. Delete automation sequences
      const { error: sequencesError } = await supabase
        .from('email_automation_sequences')
        .delete()
        .in('lead_email', leadEmails);
      if (sequencesError) logger.warn('Error deleting automation sequences:', sequencesError);
      
      // 6. Delete messages
      const { error: messagesError } = await supabase
        .from(TABLES.MESSAGES)
        .delete()
        .in('lead_email', leadEmails);
      if (messagesError) logger.warn('Error deleting messages:', messagesError);
      
      // 7. Delete conversations
      const { error: conversationsError } = await supabase
        .from(TABLES.CONVERSATIONS)
        .delete()
        .in('lead_email', leadEmails);
      if (conversationsError) logger.warn('Error deleting conversations:', conversationsError);
      
      // 8. Delete meetings
      const { error: meetingsError } = await supabase
        .from(TABLES.MEETINGS)
        .delete()
        .in('lead_email', leadEmails);
      if (meetingsError) logger.warn('Error deleting meetings:', meetingsError);
      
      // 9. Delete tasks
      const { error: tasksError } = await supabase
        .from(TABLES.TASKS)
        .delete()
        .in('lead_email', leadEmails);
      if (tasksError) logger.warn('Error deleting tasks:', tasksError);
      
      // 10. Delete notes
      const { error: notesError } = await supabase
        .from(TABLES.NOTES)
        .delete()
        .in('lead_email', leadEmails);
      if (notesError) logger.warn('Error deleting notes:', notesError);
      
      // 11. Delete lead events
      const { error: eventsError } = await supabase
        .from(TABLES.LEAD_EVENTS)
        .delete()
        .in('lead_email', leadEmails);
      if (eventsError) logger.warn('Error deleting lead events:', eventsError);
      
      // 12. Delete lead reply detection
      const { error: replyDetectionError } = await supabase
        .from('lead_reply_detection')
        .delete()
        .in('lead_email', leadEmails);
      if (replyDetectionError) logger.warn('Error deleting reply detection:', replyDetectionError);
      
      // 13. Delete lead channel activities
      const { error: channelError } = await supabase
        .from('lead_channel_activities')
        .delete()
        .in('lead_email', leadEmails);
      if (channelError) logger.warn('Error deleting channel activities:', channelError);
      
      // 14. Delete campaign lead assignments
      const { error: assignmentsError } = await supabase
        .from('campaign_lead_assignments')
        .delete()
        .in('lead_email', leadEmails);
      if (assignmentsError) logger.warn('Error deleting campaign assignments:', assignmentsError);
      
      // 15. Delete contact details
      const { error: contactError } = await supabase
        .from('contact_details')
        .delete()
        .in('lead_email', leadEmails);
      if (contactError) logger.warn('Error deleting contact details:', contactError);
      
      // 16. Delete LinkedIn-related records
      const { error: linkedinSeqError } = await supabase
        .from('linkedin_automation_sequences')
        .delete()
        .in('lead_email', leadEmails);
      if (linkedinSeqError) logger.warn('Error deleting LinkedIn sequences:', linkedinSeqError);
      
      const { error: linkedinConnError } = await supabase
        .from('linkedin_connections')
        .delete()
        .in('lead_email', leadEmails);
      if (linkedinConnError) logger.warn('Error deleting LinkedIn connections:', linkedinConnError);
      
      const { error: linkedinMsgError } = await supabase
        .from('linkedin_messages')
        .delete()
        .in('lead_email', leadEmails);
      if (linkedinMsgError) logger.warn('Error deleting LinkedIn messages:', linkedinMsgError);
      
      // 17. Delete lead list items (by lead_id)
      const { error: listItemsError } = await supabase
        .from('lead_list_items')
        .delete()
        .in('lead_id', leadIds);
      if (listItemsError) logger.warn('Error deleting lead list items:', listItemsError);
      
      // Finally, delete the leads themselves
      const { error: deleteError } = await supabase
        .from(TABLES.LEADS)
        .delete()
        .in('id', leadIds);
      
      if (deleteError) throw deleteError;
      
      logger.log(`✅ Successfully deleted ${leadEmails.length} leads and all related records`);
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.filter(lead => !leadIds.includes(lead.id))
      );
      
      return { success: true, deletedCount: leadIds.length };
    } catch (error) {
      logger.error('Error deleting leads:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Save readNotifications to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('readNotifications', JSON.stringify([...readNotifications]));
    } catch (error) {
      logger.error('Error saving read notifications:', error);
    }
  }, [readNotifications]);

  // Save readInboxMessages to localStorage whenever it changes (user-specific)
  useEffect(() => {
    if (currentUser?.email && readInboxMessages.size > 0) {
      try {
        const storageKey = `readInboxMessages_${currentUser.email}`;
        // Save as object with timestamps for new reply detection
        const objToSave = Object.fromEntries(readInboxMessages);
        localStorage.setItem(storageKey, JSON.stringify(objToSave));
      } catch (error) {
        logger.error('Error saving read inbox messages:', error);
      }
    }
  }, [readInboxMessages, currentUser?.email]);

  // Timezone management functions
  const updateUserTimezone = (timezone) => {
    setUserTimezone(timezone);
    localStorage.setItem('userTimezone', timezone);
  };

  const formatDateInUserTimezone = (date, options = {}) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // CRITICAL FIX: CRM displays everything in Helsinki timezone
    return dateObj.toLocaleString('en-US', {
      timeZone: 'Europe/Helsinki',
      ...options
    });
  };

  const formatTimeInUserTimezone = (time, date = null) => {
    if (!time) return time;
    
    // ULTIMATE FIX: Direct time formatting without Date object creation
    // Database stores Helsinki time, CRM displays Helsinki time - NO CONVERSION
    if (date) {
      // Parse and format Helsinki time directly without Date object
      const [hours, minutes] = time.split(':').map(Number);
      
      // Convert to 12-hour format manually to avoid Date object timezone issues
      const hour12 = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedTime = `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
      
      return formattedTime + ' (Helsinki)';
    }
    
    // For time-only display, just return the time as-is
    return time;
  };

  // Notification management functions
  const markNotificationAsRead = (notificationId) => {
    setReadNotifications(prev => new Set([...prev, notificationId]));
  };

  const markAllNotificationsAsRead = (notificationIds) => {
    setReadNotifications(prev => new Set([...prev, ...notificationIds]));
  };

  const isNotificationRead = (notificationId) => {
    return readNotifications.has(notificationId);
  };

  const getUnreadNotificationCount = (notifications) => {
    return notifications.filter(notification => !readNotifications.has(notification.id)).length;
  };

  // Inbox message management functions
  const markInboxMessageAsRead = (messageId) => {
    setReadInboxMessages(prev => {
      const newMap = new Map(prev);
      newMap.set(messageId, new Date().toISOString());
      return newMap;
    });
  };

  const markConversationAsRead = (conversationId) => {
    setReadInboxMessages(prev => {
      const newMap = new Map(prev);
      newMap.set(conversationId, new Date().toISOString());
      return newMap;
    });
  };

  const isInboxMessageRead = (messageId) => {
    return readInboxMessages.has(messageId);
  };

  // Check if conversation is read, optionally checking against a specific message timestamp
  const isConversationRead = (conversationId, messageTimestamp = null) => {
    if (!readInboxMessages.has(conversationId)) {
      return false;
    }
    // If no message timestamp provided, just check if ever read
    if (!messageTimestamp) {
      return true;
    }
    // Check if message arrived before the read timestamp
    const readTimestamp = readInboxMessages.get(conversationId);
    return new Date(messageTimestamp) <= new Date(readTimestamp);
  };

  // Get the read timestamp for a conversation
  const getConversationReadTimestamp = (conversationId) => {
    return readInboxMessages.get(conversationId) || null;
  };

  // Remove lead from call list persistently using tags array
  const removeFromCallList = async (leadId) => {
    try {
      // Find the lead to get current tags
      const lead = leads.find(l => l.id === leadId);
      if (!lead) {
        throw new Error('Lead not found');
      }

      // Add 'removed_from_call_list' tag to existing tags
      const currentTags = lead.tags || [];
      const updatedTags = [...currentTags.filter(tag => tag !== 'removed_from_call_list'), 'removed_from_call_list'];

      // Update lead with the new tags array
      const { error } = await supabase
        .from(TABLES.LEADS)
        .update({ 
          tags: updatedTags,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) {
        logger.error('Error removing lead from call list:', error);
        throw error;
      }

      // Refresh leads data to reflect changes
      await fetchLeads();
      
      return { success: true };
    } catch (error) {
      logger.error('Error in removeFromCallList:', error);
      return { success: false, error: error.message };
    }
  };

  // Add lead to call list by removing removal tag and setting status
  const addToCallList = async (leadId) => {
    try {
      // Find the lead to get current tags
      const lead = leads.find(l => l.id === leadId);
      if (!lead) {
        throw new Error('Lead not found');
      }

      // Remove 'removed_from_call_list' tag from existing tags
      const currentTags = lead.tags || [];
      const updatedTags = currentTags.filter(tag => tag !== 'removed_from_call_list');

      // Update lead with cleaned tags and call_list status
      const { error } = await supabase
        .from(TABLES.LEADS)
        .update({ 
          tags: updatedTags,
          status: LEAD_STATUS.CALL_LIST,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) {
        logger.error('Error adding lead to call list:', error);
        throw error;
      }

      // Refresh leads data to reflect changes
      await fetchLeads();
      
      return { success: true };
    } catch (error) {
      logger.error('Error in addToCallList:', error);
      return { success: false, error: error.message };
    }
  };

  // Set up real-time subscriptions
  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to leads changes
    const leadsSubscription = supabase
      .channel('leads-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.LEADS },
        (payload) => {
          logger.log('Leads change received:', payload);
          // Add debouncing to prevent too frequent refreshes during bulk operations
          if (window.leadsRefreshTimeout) {
            clearTimeout(window.leadsRefreshTimeout);
          }
          window.leadsRefreshTimeout = setTimeout(() => {
            fetchLeads(); // Refetch leads on any change
          }, 2000); // Wait 2 seconds before refreshing
        }
      )
      .subscribe();

    // Subscribe to lead events changes
    const eventsSubscription = supabase
      .channel('events-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.LEAD_EVENTS },
        async (payload) => {
          logger.log('Events change received:', payload);
          fetchLeadEvents(); // Refetch events on any change
          
          // Trigger email automation for new lead events
          if (payload.eventType === 'INSERT' && payload.new) {
            try {
              logger.log('🔄 Processing new lead event for automation:', payload.new);
              await emailAutomationService.processLeadEvent(payload.new);
            } catch (error) {
              logger.error('Error processing lead event for automation:', error);
            }
          }
        }
      )
      .subscribe();

    // Subscribe to messages changes
    const messagesSubscription = supabase
      .channel('messages-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.MESSAGES },
        (payload) => {
          logger.log('Messages change received:', payload);
          // Update messages state if we have them loaded
          if (messages.length > 0) {
            if (payload.eventType === 'INSERT') {
              setMessages(prev => [...prev, payload.new]);
            } else if (payload.eventType === 'UPDATE') {
              setMessages(prev => prev.map(msg => 
                msg.id === payload.new.id ? payload.new : msg
              ));
            } else if (payload.eventType === 'DELETE') {
              setMessages(prev => prev.filter(msg => msg.id !== payload.old.id));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to conversations changes
    const conversationsSubscription = supabase
      .channel('conversations-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.CONVERSATIONS },
        (payload) => {
          logger.log('Conversations change received:', payload);
          if (conversations.length > 0) {
            if (payload.eventType === 'INSERT') {
              setConversations(prev => [payload.new, ...prev]);
            } else if (payload.eventType === 'UPDATE') {
              setConversations(prev => prev.map(conv => 
                conv.id === payload.new.id ? payload.new : conv
              ));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to tasks changes
    const tasksSubscription = supabase
      .channel('tasks-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.TASKS },
        (payload) => {
          logger.log('Tasks change received:', payload);
          if (tasks.length > 0) {
            if (payload.eventType === 'INSERT') {
              setTasks(prev => [payload.new, ...prev]);
            } else if (payload.eventType === 'UPDATE') {
              setTasks(prev => prev.map(task => 
                task.id === payload.new.id ? payload.new : task
              ));
            } else if (payload.eventType === 'DELETE') {
              setTasks(prev => prev.filter(task => task.id !== payload.old.id));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to notes changes
    const notesSubscription = supabase
      .channel('notes-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.NOTES },
        (payload) => {
          logger.log('Notes change received:', payload);
          if (notes.length > 0) {
            if (payload.eventType === 'INSERT') {
              setNotes(prev => [payload.new, ...prev]);
            } else if (payload.eventType === 'UPDATE') {
              setNotes(prev => prev.map(note => 
                note.id === payload.new.id ? payload.new : note
              ));
            } else if (payload.eventType === 'DELETE') {
              setNotes(prev => prev.filter(note => note.id !== payload.old.id));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to meetings changes
    const meetingsSubscription = supabase
      .channel('meetings-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.MEETINGS },
        (payload) => {
          logger.log('Meetings change received:', payload);
          if (meetings.length > 0) {
            if (payload.eventType === 'INSERT') {
              setMeetings(prev => [...prev, payload.new]);
            } else if (payload.eventType === 'UPDATE') {
              setMeetings(prev => prev.map(meeting => 
                meeting.id === payload.new.id ? payload.new : meeting
              ));
            } else if (payload.eventType === 'DELETE') {
              setMeetings(prev => prev.filter(meeting => meeting.id !== payload.old.id));
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(leadsSubscription);
      supabase.removeChannel(eventsSubscription);
      supabase.removeChannel(messagesSubscription);
      supabase.removeChannel(conversationsSubscription);
      supabase.removeChannel(tasksSubscription);
      supabase.removeChannel(notesSubscription);
      supabase.removeChannel(meetingsSubscription);
    };
  }, [currentUser]);

  // Initial data fetch and service initialization
  useEffect(() => {
    if (currentUser) {
      fetchLeads();
      fetchLeadEvents();
      fetchMessages(); // Load all messages for inbox
      
      // Initialize email automation service
      emailAutomationService.initialize()
        .then(() => {
          logger.log('✅ Email automation service initialized successfully');
        })
        .catch((error) => {
          logger.error('Error initializing email automation:', error);
        });
    }
  }, [currentUser]);

  const value = {
    leads,
    leadEvents,
    loading,
    error,
    conversations,
    messages,
    meetings,
    notes,
    tasks,
    leadEmailCounts,
    
    // Lead functions
    fetchLeads,
    fetchLeadEvents,
    getDashboardStats,
    getDashboardStatsRealtime,
    getCampaignStatsRealtime,
    getRealtimeEmailCounts,
    getDailyDashboardStats,
    getDashboardStatsDateRange,
    archiveDailyStatsToPermanent,
    filterLeads,
    updateLeadStatus,
    updateLeadContact,
    bulkUpdateLeadStatus,
    archiveLeads,
    moveToCallList,
    restoreLeads,
    deleteLeads,
    
    // Conversation functions
    fetchConversations,
    fetchMessages,
    sendMessage,
    sendEmailReply,
    
    // Meeting functions
    fetchMeetings,
    scheduleMeeting,
    syncMeetingToGoogle,
    updateMeetingWithSync,
    deleteMeetingWithSync,
    
    // Note functions
    fetchNotes,
    addNote,
    
    // Task functions
    fetchTasks,
    addTask,
    toggleTask,
    
    // Notification functions
    markNotificationAsRead,
    markAllNotificationsAsRead,
    isNotificationRead,
    getUnreadNotificationCount,
    
    // Inbox message functions
    markInboxMessageAsRead,
    markConversationAsRead,
    isInboxMessageRead,
    isConversationRead,
    
    // Call list functions
    removeFromCallList,
    addToCallList,
    
    // Sequence-based email counting
    getLeadEmailCount: (leadEmail) => leadEmailCounts[leadEmail] || 0,
    refreshLeadEmailCounts: async () => {
      try {
        const emailCounts = await advancedEmailAutomationService.getLeadEmailCounts();
        setLeadEmailCounts(emailCounts);
        return emailCounts;
      } catch (error) {
        logger.error('Error refreshing lead email counts:', error);
        return {};
      }
    },
    
    // Timezone functions
    userTimezone,
    updateUserTimezone,
    formatDateInUserTimezone,
    formatTimeInUserTimezone
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}